import { useState, useEffect, useRef } from "react";

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #f7f6f2;
    --ink: #0e0e0b;
    --ink-soft: #555550;
    --ink-faint: #b0afa8;
    --accent: #b84c2a;
    --rule: #e2e0d8;
    --serif: 'Cormorant Garamond', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--ink);
    font-family: var(--sans);
    font-size: 15px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  .fade-in {
    opacity: 0;
    transform: translateY(18px);
    animation: fadeUp 0.7s ease forwards;
  }
  @keyframes fadeUp {
    to { opacity: 1; transform: translateY(0); }
  }

  a {
    color: var(--ink);
    text-decoration: none;
    border-bottom: 1px solid var(--rule);
    transition: border-color 0.2s, color 0.2s;
  }
  a:hover { color: var(--accent); border-color: var(--accent); }

  .nav {
    position: fixed; top: 0; left: 0; right: 0;
    z-index: 100;
    padding: 20px 48px;
    display: flex; justify-content: space-between; align-items: center;
    background: rgba(247, 246, 242, 0.88);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid transparent;
    transition: border-color 0.3s;
  }
  .nav.scrolled { border-bottom-color: var(--rule); }
  .nav-name {
    font-family: var(--serif);
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0.01em;
    border: none;
  }
  .nav-links { display: flex; gap: 32px; }
  .nav-links a {
    font-size: 13px;
    font-weight: 400;
    color: var(--ink-soft);
    letter-spacing: 0.04em;
    border: none;
  }
  .nav-links a:hover { color: var(--ink); }

  .container { max-width: 720px; margin: 0 auto; padding: 0 32px; }

  /* ── Hero ── */
  .hero {
    padding: 180px 0 100px;
  }
  .hero-label {
    font-family: var(--sans);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 24px;
  }
  .hero-name {
    font-family: var(--serif);
    font-size: clamp(52px, 8vw, 82px);
    font-weight: 500;
    line-height: 1.05;
    letter-spacing: -0.01em;
    margin-bottom: 28px;
  }
  .hero-name em {
    font-style: italic;
    color: var(--ink-soft);
  }
  .hero-bio {
    font-size: 17px;
    color: var(--ink-soft);
    line-height: 1.75;
    max-width: 520px;
    font-weight: 300;
  }
  .hero-bio strong {
    color: var(--ink);
    font-weight: 500;
  }
  .hero-cta {
    margin-top: 44px;
    display: flex;
    gap: 24px;
    align-items: center;
  }
  .btn-primary {
    display: inline-block;
    background: var(--ink);
    color: var(--bg) !important;
    border: none !important;
    padding: 12px 28px;
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
    border-radius: 2px;
    transition: background 0.2s, transform 0.15s;
  }
  .btn-primary:hover { background: var(--accent); transform: translateY(-1px); }
  .btn-ghost {
    font-size: 13px;
    color: var(--ink-soft);
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--rule) !important;
  }

  /* ── Section ── */
  .section { padding: 80px 0; border-top: 1px solid var(--rule); }
  .section-label {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-bottom: 40px;
  }

  /* ── About ── */
  .about-grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 48px;
    align-items: start;
  }
  .about-aside {
    padding-top: 4px;
  }
  .about-year {
    font-family: var(--serif);
    font-size: 48px;
    font-style: italic;
    color: var(--rule);
    line-height: 1;
    margin-bottom: 12px;
  }
  .about-location {
    font-size: 12px;
    color: var(--ink-faint);
    letter-spacing: 0.08em;
  }
  .about-text {
    font-size: 16px;
    color: var(--ink-soft);
    line-height: 1.85;
    font-weight: 300;
  }
  .about-text strong { color: var(--ink); font-weight: 500; }
  .about-text p + p { margin-top: 20px; }

  /* ── Roles ── */
  .roles { display: flex; flex-direction: column; gap: 0; }
  .role {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 24px;
    padding: 28px 0;
    border-bottom: 1px solid var(--rule);
    transition: background 0.15s;
    cursor: default;
  }
  .role:first-child { border-top: 1px solid var(--rule); }
  .role-name {
    font-family: var(--serif);
    font-size: 22px;
    font-weight: 500;
    margin-bottom: 6px;
  }
  .role-desc {
    font-size: 13px;
    color: var(--ink-soft);
    font-weight: 300;
    line-height: 1.6;
    max-width: 420px;
  }
  .role-tag {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-faint);
    padding-top: 6px;
    white-space: nowrap;
  }
  .role:hover .role-name { color: var(--accent); }

  /* ── Workshops ── */
  .workshop-intro {
    font-size: 16px;
    color: var(--ink-soft);
    font-weight: 300;
    line-height: 1.8;
    margin-bottom: 36px;
    max-width: 520px;
  }
  .colleges {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 36px;
  }
  .college-tag {
    font-size: 12px;
    font-weight: 400;
    color: var(--ink-soft);
    background: white;
    border: 1px solid var(--rule);
    border-radius: 2px;
    padding: 6px 14px;
    letter-spacing: 0.03em;
  }
  .cert-callout {
    background: white;
    border: 1px solid var(--rule);
    border-left: 3px solid var(--accent);
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }
  .cert-callout-text { font-size: 14px; color: var(--ink-soft); font-weight: 300; }
  .cert-callout-text strong { color: var(--ink); font-weight: 500; }
  .cert-link {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.06em;
    white-space: nowrap;
    color: var(--accent) !important;
    border-color: var(--accent) !important;
  }

  /* ── Writing ── */
  .posts { display: flex; flex-direction: column; gap: 0; }
  .post {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 20px;
    padding: 22px 0;
    border-bottom: 1px solid var(--rule);
    align-items: center;
  }
  .post:first-child { border-top: 1px solid var(--rule); }
  .post-num {
    font-family: var(--serif);
    font-style: italic;
    font-size: 14px;
    color: var(--ink-faint);
    min-width: 24px;
  }
  .post-title {
    font-size: 15px;
    font-weight: 400;
    color: var(--ink);
  }
  .post-date { font-size: 12px; color: var(--ink-faint); }

  /* ── Connect ── */
  .connect-heading {
    font-family: var(--serif);
    font-size: 38px;
    font-weight: 500;
    font-style: italic;
    margin-bottom: 16px;
  }
  .connect-sub {
    font-size: 15px;
    color: var(--ink-soft);
    font-weight: 300;
    margin-bottom: 40px;
    line-height: 1.7;
  }
  .links-row {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
    align-items: center;
  }
  .links-row a {
    font-size: 13px;
    letter-spacing: 0.04em;
    color: var(--ink-soft);
    border-bottom: 1px solid var(--rule);
  }

  /* ── Footer ── */
  .footer {
    padding: 40px 0;
    border-top: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: var(--ink-faint);
  }

  @media (max-width: 600px) {
    .nav { padding: 18px 24px; }
    .nav-links { gap: 20px; }
    .container { padding: 0 24px; }
    .about-grid { grid-template-columns: 1fr; gap: 24px; }
    .about-year { font-size: 36px; }
    .cert-callout { flex-direction: column; align-items: flex-start; }
    .footer { flex-direction: column; gap: 8px; }
  }
`;

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return scrolled;
}

function useFadeIn(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.animationDelay = e.target.dataset.delay || "0s";
          e.target.classList.add("fade-in");
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.12 }
    );
    ref.current.querySelectorAll("[data-animate]").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [ref]);
}

export default function Home() {
  const scrolled = useScrolled();
  const pageRef = useRef(null);
  useFadeIn(pageRef);

  return (
    <>
      <style>{style}</style>
      <div ref={pageRef}>

        {/* Nav */}
        <nav className={`nav${scrolled ? " scrolled" : ""}`}>
          <a href="/" className="nav-name">Kuldeep Anand</a>
          <div className="nav-links">
            <a href="#work">Work</a>
            <a href="#workshops">Workshops</a>
            <a href="#connect">Connect</a>
          </div>
        </nav>

        <div className="container">

          {/* Hero */}
          <section className="hero">
            <div className="hero-label fade-in" data-animate data-delay="0s">
              Founder & CEO, Visomni
            </div>
            <h1 className="hero-name fade-in" data-animate data-delay="0.1s">
              Kuldeep<br /><em>Anand</em>
            </h1>
            <p className="hero-bio fade-in" data-animate data-delay="0.2s">
              Building <strong>Visomni</strong>. Computer vision and digital twins for steel
              and power plants. We locate the fuel industry loses without knowing it.
              I teach <strong>Generative AI & Vibe Coding</strong> to students and professionals
              who want to ship real products.
            </p>
            <div className="hero-cta fade-in" data-animate data-delay="0.3s">
              <a href="#connect" className="btn-primary">Get in touch</a>
              <a href="#work" className="btn-ghost">See my work ↓</a>
            </div>
          </section>

          {/* About */}
          <section className="section" id="about">
            <div className="section-label" data-animate data-delay="0s">About</div>
            <div className="about-grid">
              <div className="about-aside" data-animate>
                <div className="about-year">10+</div>
                <div className="about-location">India</div>
              </div>
              <div className="about-text" data-animate data-delay="0.1s">
                <p>
                  Steel and power plants leak fuel at every stage of production. Operators
                  rarely know how much because they have no way to watch it happen.
                  <strong> Visomni</strong> gives them that view. Computer vision and digital
                  twins map the loss in real time, and plants recover it.
                </p>
                <p>
                  I teach Generative AI and Vibe Coding to students and professionals across
                  India. The tools exist. Most people lack access to someone who can show
                  them how to use those tools to build real products.
                </p>
              </div>
            </div>
          </section>

          {/* Work */}
          <section className="section" id="work">
            <div className="section-label" data-animate>What I do</div>
            <div className="roles">
              {[
                {
                  name: "Visomni",
                  desc: "Founder & CEO. Computer vision and digital twins for steel and power plants. Operators lose fuel they cannot track. We build the visibility layer. Clients recover measurable energy every quarter.",
                  tag: "Company",
                  delay: "0s",
                },
                {
                  name: "Generative AI Workshops",
                  desc: "Workshops on Generative AI and Vibe Coding at colleges and organisations across India. I teach from practice. Participants leave with working projects.",
                  tag: "Workshops",
                  delay: "0.05s",
                },
                {
                  name: "Vibe Coding: Build Anything with AI",
                  desc: "A course for people who want to ship software without an engineering background. Covers prompting, LLM APIs, and agent workflows. Students go from idea to working product.",
                  tag: "Course",
                  delay: "0.1s",
                },
              ].map(r => (
                <div className="role" key={r.name} data-animate data-delay={r.delay}>
                  <div>
                    <div className="role-name">{r.name}</div>
                    <div className="role-desc">{r.desc}</div>
                  </div>
                  <div className="role-tag">{r.tag}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Workshops */}
          <section className="section" id="workshops">
            <div className="section-label" data-animate>Workshops & Courses</div>
            <p className="workshop-intro" data-animate data-delay="0.05s">
              I run Generative AI and Vibe Coding workshops at colleges and organisations
              across India. Students build projects. Professionals leave with skills they
              use the following week.
            </p>
            {/* <div className="colleges" data-animate data-delay="0.1s">
              {["MIT Pune", "VIT Vellore", "SRM Chennai", "BITS Pilani", "Manipal", "NMIMS Mumbai", "PES Bangalore", "Your College →"].map(c => (
                <span className="college-tag" key={c}>{c}</span>
              ))}
            </div> */}
            <div className="cert-callout" data-animate data-delay="0.15s">
              <div className="cert-callout-text">
                <strong>Attended a workshop?</strong> Download your verified certificate —
                issued in collaboration with your college or organisation.
              </div>
              <a href="https://certificate.kuldeepanand.com" className="cert-link" target="_blank" rel="noreferrer">
                Get certificate ↗
              </a>
            </div>
          </section>

          {/* Writing — hidden
          <section className="section" id="writing">
            <div className="section-label" data-animate>Thinking out loud</div>
            <div className="posts">
              {[
                { n: "01", title: "Vibe Coding is a shift in who gets to build", date: "Mar 2026" },
                { n: "02", title: "You don't need to code to build with AI", date: "Feb 2026" },
                { n: "03", title: "Why I started Visomni", date: "Nov 2025" },
              ].map((p, i) => (
                <div className="post" key={p.n} data-animate data-delay={`${i * 0.05}s`}>
                  <span className="post-num">{p.n}</span>
                  <span className="post-title">{p.title}</span>
                  <span className="post-date">{p.date}</span>
                </div>
              ))}
            </div>
          </section> */}

          {/* Connect */}
          <section className="section" id="connect" style={{ paddingBottom: "120px" }}>
            <div className="connect-heading" data-animate>
              Let's talk.
            </div>
            <p className="connect-sub" data-animate data-delay="0.07s">
              Working in industrial AI. Curious about Visomni. Want a workshop at your
              college or company. Reach out.
            </p>
            <div className="links-row" data-animate data-delay="0.12s">
              <a href="https://calendar.app.google/gJgd45xcQxpdzwr3A" target="_blank" rel="noreferrer">30 min with Kuldeep</a>
              <a href="mailto:iam@kuldeepanand.com">iam@kuldeepanand.com</a>
              <a href="https://linkedin.com/in/kuldeepanand" target="_blank" rel="noreferrer">LinkedIn</a>
              {/* <a href="https://twitter.com/kuldeepanand" target="_blank" rel="noreferrer">Twitter / X</a> */}
              <a href="https://visomni.com" target="_blank" rel="noreferrer">Visomni ↗</a>
            </div>
          </section>

          {/* Footer */}
          <footer className="footer" data-animate>
            <span>© 2026 Kuldeep Anand</span>
            <span>kuldeepanand.com</span>
          </footer>

        </div>
      </div>
    </>
  );
}
